import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Link,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {
  useGetAutoOutgoingCandidatesQuery,
  useGetKsefOutgoingEnumsQuery,
  useGetOutgoingRecordsQuery,
  useGetSemanticEventsQuery,
  useGetSemanticMetricsQuery,
  useLazyGetOutgoingRecordsQuery,
  useLazyGetOutgoingSnapshotPreviewQuery,
  useLazyGetOutgoingUpoDownloadUrlQuery,
  usePollOutgoingInvoiceStatusMutation,
  usePrepareOutgoingInvoiceMutation,
  useSubmitOutgoingInvoiceMutation,
  useBackfillKsefInvoiceNumbersMutation,
} from '../Api/ksefOutgoingApi';
import {
  KsefOutgoingEligibilityDecision,
  KsefOutgoingEligibilityInputDto,
  KsefOutgoingCandidatesQuery,
  KsefOutgoingRecordDto,
  OutgoingSnapshotPreviewResult,
} from '../Types/ksefOutgoing.types';
import {
  buildEligibilityInput,
  defaultKsefOutgoingPayloadForm,
  KsefOutgoingPayloadForm,
} from '../Utils/ksefOutgoingPayload';
import {
  getKsefOutgoingErrorCode,
  getKsefOutgoingErrorMessage,
} from '../Utils/ksefOutgoingErrorMapping';
import { buildKsefOutgoingLabelResolvers } from '../Utils/ksefOutgoingLabels';
import KsefOutgoingDetailsPanel from '../Components/KsefOutgoingDetailsPanel';
import KsefOutgoingTestEvidencePanel from '../Components/KsefOutgoingTestEvidencePanel';
import { useGetKsefOutgoingInvoiceLookupQuery } from '../Service/invoiceLookupApi';
import {
  createKsefOutgoingRecordsPdfBlob,
  downloadKsefOutgoingRecordsPdf,
} from '../Utils/ksefOutgoingRecordsPdfExport';

const VAT_EU_FIELD_OPTIONS = [
  { value: '', label: 'Z faktury / bez nadpisania' },
  { value: 'true', label: 'Tak' },
  { value: 'false', label: 'Nie' },
] as const;

const ELIGIBILITY_OVERRIDE_OPTIONS = [
  { value: 'none', label: 'Brak nadpisania' },
  { value: 'forceExclude', label: 'Wyklucz z auto-kwalifikacji' },
  { value: 'forceInclude', label: 'Wymuś kwalifikację' },
] as const;

const toVatEuFieldValue = (value?: boolean | null): '' | 'true' | 'false' => {
  if (typeof value !== 'boolean') {
    return '';
  }

  return value ? 'true' : 'false';
};

const toOptionalPayloadText = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

const decisionColor = (
  decision: KsefOutgoingEligibilityDecision
): 'success' | 'warning' | 'error' => {
  if (decision === 'eligibleAuto') {
    return 'success';
  }

  if (decision === 'manualOnly') {
    return 'warning';
  }

  return 'error';
};

const toLocalDateTimeInputValue = (date: Date): string => {
  const adjusted = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return adjusted.toISOString().slice(0, 16);
};

const toUtcIso = (value: string): string | undefined => {
  if (!value) {
    return undefined;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

interface SemanticFilterState {
  fromUtc?: string;
  toUtc?: string;
  take: number;
}

const KSEF_POLAND_ROLLOUT_ISSUE_DATE_FROM = '2026-04-01';

const KsefOutgoingConsolePage = () => {
  const defaultToUtc = useMemo(() => new Date(), []);
  const defaultFromUtc = useMemo(
    () => new Date(defaultToUtc.getTime() - 7 * 24 * 60 * 60 * 1000),
    [defaultToUtc]
  );

  const [query, setQuery] = useState<KsefOutgoingCandidatesQuery>({
    take: 200,
    issueDateFrom: KSEF_POLAND_ROLLOUT_ISSUE_DATE_FROM,
  });
  const [semanticFromUtc, setSemanticFromUtc] = useState<string>(
    toLocalDateTimeInputValue(defaultFromUtc)
  );
  const [semanticToUtc, setSemanticToUtc] = useState<string>(toLocalDateTimeInputValue(defaultToUtc));
  const [semanticTake, setSemanticTake] = useState<number>(100);
  const [appliedSemanticFilters, setAppliedSemanticFilters] = useState<SemanticFilterState>(() => ({
    fromUtc: toUtcIso(toLocalDateTimeInputValue(defaultFromUtc)),
    toUtc: toUtcIso(toLocalDateTimeInputValue(defaultToUtc)),
    take: 100,
  }));
  const [outgoingRecordsPage, setOutgoingRecordsPage] = useState<number>(0);
  const [outgoingRecordsPageSize, setOutgoingRecordsPageSize] = useState<number>(25);
  const [outgoingRecordsDateFromInput, setOutgoingRecordsDateFromInput] = useState<string>('');
  const [outgoingRecordsDateToInput, setOutgoingRecordsDateToInput] = useState<string>('');
  const [appliedOutgoingRecordsDateFrom, setAppliedOutgoingRecordsDateFrom] = useState<
    string | undefined
  >(undefined);
  const [appliedOutgoingRecordsDateTo, setAppliedOutgoingRecordsDateTo] = useState<string | undefined>(
    undefined
  );
  const [isExportingOutgoingRecordsPdf, setIsExportingOutgoingRecordsPdf] = useState<boolean>(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string>('');
  const [payloadForm, setPayloadForm] = useState<KsefOutgoingPayloadForm>(
    defaultKsefOutgoingPayloadForm
  );
  const [lastActionLabel, setLastActionLabel] = useState<string>('');
  const [lastActionResult, setLastActionResult] = useState<string>('');
  const [snapshotPreview, setSnapshotPreview] = useState<OutgoingSnapshotPreviewResult | null>(null);

  const { data: enumsResult } = useGetKsefOutgoingEnumsQuery();

  const {
    data: candidatesResult,
    isLoading: isCandidatesLoading,
    isFetching: isCandidatesFetching,
    error: candidatesError,
    refetch: refetchCandidates,
  } = useGetAutoOutgoingCandidatesQuery(
    {
      take: query.take,
      issueDateFrom: query.issueDateFrom || undefined,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );
  const outgoingRecordsQuery = useMemo(
    () => ({
      pageNumber: outgoingRecordsPage + 1,
      pageSize: outgoingRecordsPageSize,
      ...(appliedOutgoingRecordsDateFrom
        ? { createdDateFrom: appliedOutgoingRecordsDateFrom }
        : {}),
      ...(appliedOutgoingRecordsDateTo ? { createdDateTo: appliedOutgoingRecordsDateTo } : {}),
    }),
    [
      appliedOutgoingRecordsDateFrom,
      appliedOutgoingRecordsDateTo,
      outgoingRecordsPage,
      outgoingRecordsPageSize,
    ]
  );
  const {
    data: outgoingRecordsResult,
    isFetching: isOutgoingRecordsFetching,
    error: outgoingRecordsError,
    refetch: refetchOutgoingRecords,
  } = useGetOutgoingRecordsQuery(outgoingRecordsQuery, {
    refetchOnMountOrArgChange: true,
  });

  const [prepareOutgoingInvoice, prepareState] = usePrepareOutgoingInvoiceMutation();
  const [submitOutgoingInvoice, submitState] = useSubmitOutgoingInvoiceMutation();
  const [pollOutgoingStatus, pollState] = usePollOutgoingInvoiceStatusMutation();
  const [backfillKsefNumbers, backfillState] = useBackfillKsefInvoiceNumbersMutation();
  const [loadOutgoingRecordsPage] = useLazyGetOutgoingRecordsQuery();
  const [loadUpoUrl, upoState] = useLazyGetOutgoingUpoDownloadUrlQuery();
  const [loadOutgoingSnapshotPreview, snapshotPreviewState] = useLazyGetOutgoingSnapshotPreviewQuery();
  const selectedInvoiceIdValue = useMemo(() => {
    const parsed = Number(selectedInvoiceId);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [selectedInvoiceId]);

  const {
    data: selectedInvoiceLookup,
    isLoading: isInvoiceLookupLoading,
    isFetching: isInvoiceLookupFetching,
    error: invoiceLookupError,
  } = useGetKsefOutgoingInvoiceLookupQuery(selectedInvoiceIdValue ?? 0, {
    skip: selectedInvoiceIdValue === null,
  });

  const semanticMetricsQuery = useMemo(
    () => ({
      fromUtc: appliedSemanticFilters.fromUtc,
      toUtc: appliedSemanticFilters.toUtc,
    }),
    [appliedSemanticFilters.fromUtc, appliedSemanticFilters.toUtc]
  );

  const semanticEventsQuery = useMemo(
    () => ({
      fromUtc: appliedSemanticFilters.fromUtc,
      toUtc: appliedSemanticFilters.toUtc,
      take: appliedSemanticFilters.take,
    }),
    [appliedSemanticFilters.fromUtc, appliedSemanticFilters.take, appliedSemanticFilters.toUtc]
  );

  const {
    data: semanticMetricsResult,
    isFetching: isSemanticMetricsFetching,
    error: semanticMetricsError,
    refetch: refetchSemanticMetrics,
  } = useGetSemanticMetricsQuery(semanticMetricsQuery, {
    refetchOnMountOrArgChange: true,
  });

  const {
    data: semanticEventsResult,
    isFetching: isSemanticEventsFetching,
    error: semanticEventsError,
    refetch: refetchSemanticEvents,
  } = useGetSemanticEventsQuery(semanticEventsQuery, {
    refetchOnMountOrArgChange: true,
  });

  const applySemanticFilters = () => {
    setAppliedSemanticFilters({
      fromUtc: toUtcIso(semanticFromUtc),
      toUtc: toUtcIso(semanticToUtc),
      take: semanticTake,
    });
  };

  const busy =
    prepareState.isLoading ||
    submitState.isLoading ||
    pollState.isLoading ||
    upoState.isFetching ||
    snapshotPreviewState.isFetching;

  const selectedCandidate = useMemo(
    () =>
      candidatesResult?.candidates.find(
        (candidate) => String(candidate.invoiceId) === selectedInvoiceId
      ),
    [candidatesResult?.candidates, selectedInvoiceId]
  );
  const selectedOutgoingRecord = useMemo<KsefOutgoingRecordDto | undefined>(
    () =>
      outgoingRecordsResult?.records.find(
        (record) => String(record.invoiceId) === selectedInvoiceId
      ),
    [outgoingRecordsResult?.records, selectedInvoiceId]
  );

  const selectedInvoiceNumber = useMemo(() => {
    return (
      selectedInvoiceLookup?.invoiceNumber ??
      selectedCandidate?.invoiceNumber ??
      selectedOutgoingRecord?.invoiceNumber
    );
  }, [selectedCandidate, selectedInvoiceLookup?.invoiceNumber, selectedOutgoingRecord?.invoiceNumber]);

  const resolvedEligibilitySource = useMemo(
    () => ({
      buyerCountryIso: selectedInvoiceLookup?.buyerCountryIso ?? selectedCandidate?.buyerCountryIso ?? null,
      buyerNip: selectedInvoiceLookup?.buyerNip ?? selectedCandidate?.buyerNip ?? null,
      isBuyerVatEu:
        selectedInvoiceLookup?.isBuyerVatEu ?? selectedCandidate?.isBuyerVatEu ?? null,
    }),
    [
      selectedCandidate?.buyerCountryIso,
      selectedCandidate?.buyerNip,
      selectedCandidate?.isBuyerVatEu,
      selectedInvoiceLookup?.buyerCountryIso,
      selectedInvoiceLookup?.buyerNip,
      selectedInvoiceLookup?.isBuyerVatEu,
    ]
  );

  const displayedBuyerCountryIso =
    payloadForm.buyerCountryIso ?? resolvedEligibilitySource.buyerCountryIso ?? '';
  const displayedBuyerNip = payloadForm.buyerNip ?? resolvedEligibilitySource.buyerNip ?? '';
  const displayedBuyerVatEu =
    payloadForm.isBuyerVatEu ?? toVatEuFieldValue(resolvedEligibilitySource.isBuyerVatEu);
  const isEligibilityOverrideEnabled = payloadForm.eligibilityOverride !== 'none';
  const manualInvoiceLookupWarning =
    selectedInvoiceIdValue !== null &&
    !selectedCandidate &&
    invoiceLookupError &&
    !isInvoiceLookupLoading &&
    !isInvoiceLookupFetching;

  useEffect(() => {
    setPayloadForm(defaultKsefOutgoingPayloadForm);
    setSnapshotPreview(null);
  }, [selectedInvoiceId]);

  const labelResolvers = useMemo(() => buildKsefOutgoingLabelResolvers(enumsResult), [enumsResult]);

  const parsedActionResult = useMemo(() => {
    if (!lastActionResult) {
      return undefined;
    }

    try {
      return JSON.parse(lastActionResult) as {
        state?: string;
        ksefStatusCode?: number;
        ksefStatusDescription?: string;
        hasUpoFile?: boolean;
        correlationId?: string;
        errorCode?: string;
        lastPolledAtUtc?: string;
      };
    } catch {
      return undefined;
    }
  }, [lastActionResult]);

  const selectedRecordSnapshot = useMemo(
    () =>
      selectedOutgoingRecord
        ? {
            state: selectedOutgoingRecord.state,
            hasUpoFile: selectedOutgoingRecord.hasUpoFile,
            correlationId: selectedOutgoingRecord.correlationId ?? undefined,
            errorCode: selectedOutgoingRecord.lastErrorCode ?? undefined,
            lastPolledAtUtc: selectedOutgoingRecord.lastPolledAtUtc ?? undefined,
          }
        : undefined,
    [selectedOutgoingRecord]
  );

  const detailsSnapshot = parsedActionResult ?? selectedRecordSnapshot;

  const setResult = (label: string, payload: unknown) => {
    setLastActionLabel(label);
    setLastActionResult(JSON.stringify(payload, null, 2));
  };

  const toDiagnosticError = (error: unknown): unknown => {
    if (typeof error !== 'object' || error === null) {
      return error;
    }

    const maybeError = error as { data?: unknown; status?: unknown; error?: unknown };
    return {
      status: maybeError.status,
      data: maybeError.data,
      error: maybeError.error,
    };
  };

  const buildActionErrorResult = (error: unknown, fallbackMessage: string) => ({
    message: getKsefOutgoingErrorMessage(error, fallbackMessage),
    errorCode: getKsefOutgoingErrorCode(error),
    details: toDiagnosticError(error),
  });

  const getInvoiceId = (): number | null => {
    const parsed = Number(selectedInvoiceId);
    if (!Number.isInteger(parsed) || parsed <= 0) {
      setResult('Walidacja', {
        message: 'Wybierz poprawny invoiceId przed wykonaniem akcji.',
      });
      return null;
    }

    return parsed;
  };

  const getEligibilityInput = (): KsefOutgoingEligibilityInputDto | undefined | null => {
    const eligibilityInput = buildEligibilityInput(payloadForm, resolvedEligibilitySource);

    if (!eligibilityInput) {
      return undefined;
    }

    if (
      !eligibilityInput.buyerCountryIso ||
      typeof eligibilityInput.isBuyerVatEu !== 'boolean'
    ) {
      setResult('Walidacja', {
        message:
          'Jeśli wysyłasz ręczne dane kwalifikacji, uzupełnij kraj nabywcy i status VAT UE albo wróć do danych z faktury.',
      });
      return null;
    }

    return eligibilityInput;
  };

  const fetchSnapshotPreview = async (invoiceId: number): Promise<void> => {
    try {
      const result = await loadOutgoingSnapshotPreview(invoiceId, true).unwrap();
      setSnapshotPreview(result);
    } catch (error) {
      setSnapshotPreview(null);
      setResult(
        'Podgląd XML: błąd',
        buildActionErrorResult(error, 'Nie udało się pobrać podglądu XML FA(3).')
      );
    }
  };

  const handlePrepare = async () => {
    const invoiceId = getInvoiceId();
    if (!invoiceId) {
      return;
    }

    const eligibilityInput = getEligibilityInput();
    if (eligibilityInput === null) {
      return;
    }

    try {
      const result = await prepareOutgoingInvoice({
        invoiceId,
        eligibilityInput,
      }).unwrap();
      setResult('Przygotuj: sukces', result);
      await fetchSnapshotPreview(invoiceId);
      refetchCandidates();
    } catch (error) {
      setResult(
        'Przygotuj: błąd',
        buildActionErrorResult(error, 'Nie udało się przygotować dokumentu KSeF.')
      );
    }
  };

  const handleSubmit = async () => {
    const invoiceId = getInvoiceId();
    if (!invoiceId) {
      return;
    }

    const eligibilityInput = getEligibilityInput();
    if (eligibilityInput === null) {
      return;
    }

    try {
      const result = await submitOutgoingInvoice({
        invoiceId,
        eligibilityInput,
      }).unwrap();
      setResult('Wyślij: sukces', result);
      refetchCandidates();
    } catch (error) {
      setResult(
        'Wyślij: błąd',
        buildActionErrorResult(error, 'Nie udało się wysłać dokumentu do KSeF.')
      );
    }
  };

  const handlePollStatus = async () => {
    const invoiceId = getInvoiceId();
    if (!invoiceId) {
      return;
    }

    try {
      const result = await pollOutgoingStatus(invoiceId).unwrap();
      setResult('Odśwież status: sukces', result);
      refetchCandidates();
    } catch (error) {
      setResult(
        'Odśwież status: błąd',
        buildActionErrorResult(error, 'Nie udało się pobrać statusu wysyłki KSeF.')
      );
    }
  };

  const handleGetUpoUrl = async () => {
    const invoiceId = getInvoiceId();
    if (!invoiceId) {
      return;
    }

    try {
      const result = await loadUpoUrl(invoiceId, true).unwrap();
      setResult('Pobierz URL UPO: sukces', result);
    } catch (error) {
      setResult(
        'Pobierz URL UPO: błąd',
        buildActionErrorResult(error, 'Nie udało się pobrać linku UPO.')
      );
    }
  };

  const handleGetSnapshotPreview = async () => {
    const invoiceId = getInvoiceId();
    if (!invoiceId) {
      return;
    }

    await fetchSnapshotPreview(invoiceId);
  };

  const handleApplyOutgoingRecordsFilters = () => {
    if (
      outgoingRecordsDateFromInput &&
      outgoingRecordsDateToInput &&
      outgoingRecordsDateFromInput > outgoingRecordsDateToInput
    ) {
      setResult('Filtr tabeli: błąd', {
        message: "Pole 'Data utworzenia od' nie może być późniejsze niż 'Data utworzenia do'.",
      });
      return;
    }

    setOutgoingRecordsPage(0);
    setAppliedOutgoingRecordsDateFrom(outgoingRecordsDateFromInput || undefined);
    setAppliedOutgoingRecordsDateTo(outgoingRecordsDateToInput || undefined);
  };

  const handleResetOutgoingRecordsFilters = () => {
    setOutgoingRecordsDateFromInput('');
    setOutgoingRecordsDateToInput('');
    setAppliedOutgoingRecordsDateFrom(undefined);
    setAppliedOutgoingRecordsDateTo(undefined);
    setOutgoingRecordsPage(0);
  };

  const handleOutgoingRecordsPageChange = (newPage: number) => {
    setOutgoingRecordsPage(newPage);
  };

  const handleOutgoingRecordsPageSizeChange = (newPageSize: number) => {
    setOutgoingRecordsPageSize(newPageSize);
    setOutgoingRecordsPage(0);
  };

  const handleDownloadOutgoingRecordsPdf = async () => {
    const loadAllOutgoingRecordsForPdf = async (): Promise<KsefOutgoingRecordDto[]> => {
      const exportPageSize = 1000;
      const baseQuery = {
        pageSize: exportPageSize,
        ...(appliedOutgoingRecordsDateFrom
          ? { createdDateFrom: appliedOutgoingRecordsDateFrom }
          : {}),
        ...(appliedOutgoingRecordsDateTo ? { createdDateTo: appliedOutgoingRecordsDateTo } : {}),
      };

      const firstPage = await loadOutgoingRecordsPage(
        {
          ...baseQuery,
          pageNumber: 1,
        },
        true
      ).unwrap();

      if (firstPage.records.length === 0 || firstPage.totalCount <= 0) {
        return [];
      }

      const pageSize = firstPage.pageSize > 0 ? firstPage.pageSize : exportPageSize;
      const totalPages = Math.ceil(firstPage.totalCount / pageSize);
      const allRecords = [...firstPage.records];

      for (let pageNumber = 2; pageNumber <= totalPages; pageNumber += 1) {
        const page = await loadOutgoingRecordsPage(
          {
            ...baseQuery,
            pageNumber,
            pageSize,
          },
          true
        ).unwrap();
        allRecords.push(...page.records);
      }

      return allRecords;
    };

    setIsExportingOutgoingRecordsPdf(true);
    try {
      const allRecords = await loadAllOutgoingRecordsForPdf();

      if (allRecords.length === 0) {
        setResult('Eksport PDF: błąd', {
          message: 'Brak rekordów do eksportu PDF.',
        });
        return;
      }

      const totalCount = allRecords.length;
      const pdfBlob = await createKsefOutgoingRecordsPdfBlob({
        records: allRecords,
        createdDateFrom: appliedOutgoingRecordsDateFrom,
        createdDateTo: appliedOutgoingRecordsDateTo,
        totalCount,
      });
      const generatedAt = new Date().toISOString().replace(/[:.]/g, '-');
      downloadKsefOutgoingRecordsPdf(pdfBlob, `KsefOutgoingInvoices_${generatedAt}.pdf`);
    } catch (error) {
      const fallbackMessage = 'Nie udało się wygenerować PDF tabeli KsefOutgoingInvoices.';
      const message = error instanceof Error && error.message ? error.message : fallbackMessage;
      setResult('Eksport PDF: błąd', {
        message,
      });
    } finally {
      setIsExportingOutgoingRecordsPdf(false);
    }
  };

  const candidates = candidatesResult?.candidates ?? [];
  const outgoingRecords = outgoingRecordsResult?.records ?? [];
  const outgoingRecordsTotalCount = outgoingRecordsResult?.totalCount ?? outgoingRecords.length;

  return (
    <Stack spacing={3}>
      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Typography variant="h5">KSeF Outgoing - Konsola manualna</Typography>
            <Button
              variant="outlined"
              color="warning"
              size="small"
              disabled={backfillState.isLoading}
              onClick={() => backfillKsefNumbers()}
            >
              {backfillState.isLoading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
              Backfill numerów KSeF
            </Button>
          </Stack>
          {backfillState.isSuccess && backfillState.data && (
            <Alert severity="success">
              Backfill zakończony: przetworzono {backfillState.data.processed}, uzupełniono{' '}
              {backfillState.data.updated}, pominięto {backfillState.data.skipped}.
            </Alert>
          )}
          {backfillState.isError && (
            <Alert severity="error">Błąd backfill — sprawdź logi serwera.</Alert>
          )}
          <Typography color="text.secondary">
            Tryb manual-first: brak automatyzacji. Każda akcja wykonywana ręcznie przez operatora.
          </Typography>

          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                label="Limit"
                type="number"
                fullWidth
                value={query.take ?? 200}
                onChange={(event) =>
                  setQuery((prev) => ({
                    ...prev,
                    take: Math.max(1, Number(event.target.value || 1)),
                  }))
                }
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                label="Data wystawienia od"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={query.issueDateFrom ?? ''}
                onChange={(event) =>
                  setQuery((prev) => ({
                    ...prev,
                    issueDateFrom: event.target.value,
                  }))
                }
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack direction="row" spacing={2}>
                <Button variant="contained" onClick={() => refetchCandidates()}>
                  Odśwież kandydatów
                </Button>
                {(isCandidatesLoading || isCandidatesFetching) && <CircularProgress size={24} />}
              </Stack>
            </Grid>
          </Grid>

          {candidatesError && (
            <Alert severity="error">
              {getKsefOutgoingErrorMessage(
                candidatesError,
                'Nie udało się pobrać kandydatów do wysyłki KSeF.'
              )}
            </Alert>
          )}

          <Stack direction="row" spacing={2}>
            <Chip label={`Przeanalizowane: ${candidatesResult?.evaluatedCount ?? 0}`} />
            <Chip color="success" label={`Kwalifikujące się: ${candidatesResult?.eligibleCount ?? 0}`} />
          </Stack>

          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID faktury</TableCell>
                  <TableCell>Numer faktury</TableCell>
                  <TableCell>Data wystawienia</TableCell>
                  <TableCell>Kraj</TableCell>
                  <TableCell>VAT EU</TableCell>
                  <TableCell>NIP</TableCell>
                  <TableCell>Decyzja</TableCell>
                  <TableCell>Kody decyzji</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {candidates.map((candidate) => (
                  <TableRow
                    key={candidate.invoiceId}
                    data-testid={`ksef-candidate-row-${candidate.invoiceId}`}
                    hover
                    selected={String(candidate.invoiceId) === selectedInvoiceId}
                    onClick={() => setSelectedInvoiceId(String(candidate.invoiceId))}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{candidate.invoiceId}</TableCell>
                    <TableCell>{candidate.invoiceNumber}</TableCell>
                    <TableCell>{candidate.issueDate}</TableCell>
                    <TableCell>{candidate.buyerCountryIso ?? '-'}</TableCell>
                    <TableCell>
                      {typeof candidate.isBuyerVatEu === 'boolean'
                        ? String(candidate.isBuyerVatEu)
                        : '-'}
                    </TableCell>
                    <TableCell>{candidate.buyerNip ?? '-'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={decisionColor(candidate.eligibilityDecision.decision)}
                        label={
                          labelResolvers.decisionLabel(candidate.eligibilityDecision.decision) ??
                          candidate.eligibilityDecision.decision
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {labelResolvers
                        .reasonCodesLabel(candidate.eligibilityDecision.reasonCodes)
                        .join(', ') || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Stack>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="h6">Tabela KsefOutgoingInvoices</Typography>
            <Button variant="outlined" onClick={() => refetchOutgoingRecords()}>
              Odśwież tabelę wysyłek
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<PictureAsPdfIcon />}
              onClick={() => void handleDownloadOutgoingRecordsPdf()}
              disabled={
                isOutgoingRecordsFetching ||
                isExportingOutgoingRecordsPdf ||
                outgoingRecords.length === 0
              }
            >
              {isExportingOutgoingRecordsPdf ? 'Generowanie PDF...' : 'Pobierz PDF'}
            </Button>
            {isOutgoingRecordsFetching && <CircularProgress size={20} />}
          </Stack>

          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                label="Data utworzenia od"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={outgoingRecordsDateFromInput}
                onChange={(event) => setOutgoingRecordsDateFromInput(event.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                label="Data utworzenia do"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={outgoingRecordsDateToInput}
                onChange={(event) => setOutgoingRecordsDateToInput(event.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack direction="row" spacing={2}>
                <Button variant="contained" onClick={handleApplyOutgoingRecordsFilters}>
                  Filtruj tabelę
                </Button>
                <Button variant="text" onClick={handleResetOutgoingRecordsFilters}>
                  Wyczyść filtry
                </Button>
              </Stack>
            </Grid>
          </Grid>

          {outgoingRecordsError && (
            <Alert severity="error">
              {getKsefOutgoingErrorMessage(
                outgoingRecordsError,
                'Nie udało się pobrać historii tabeli KsefOutgoingInvoices.'
              )}
            </Alert>
          )}

          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>OutgoingId</TableCell>
                  <TableCell>InvoiceId</TableCell>
                  <TableCell>Numer faktury</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Snapshot v.</TableCell>
                  <TableCell>Ref KSeF</TableCell>
                  <TableCell>Numer KSeF</TableCell>
                  <TableCell>Ref sesji</TableCell>
                  <TableCell>UPO</TableCell>
                  <TableCell>UpoFilePath</TableCell>
                  <TableCell>Utworzono (UTC)</TableCell>
                  <TableCell>Wysłano (UTC)</TableCell>
                  <TableCell>Zaakceptowano (UTC)</TableCell>
                  <TableCell>Odrzucono (UTC)</TableCell>
                  <TableCell>Kod błędu</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {outgoingRecords.map((record) => (
                  <TableRow
                    key={record.outgoingId}
                    data-testid={`ksef-outgoing-record-row-${record.outgoingId}`}
                    hover
                    selected={String(record.invoiceId) === selectedInvoiceId}
                    onClick={() => setSelectedInvoiceId(String(record.invoiceId))}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{record.outgoingId}</TableCell>
                    <TableCell>{record.invoiceId}</TableCell>
                    <TableCell>{record.invoiceNumber || '-'}</TableCell>
                    <TableCell>
                      {labelResolvers.stateLabel(record.state) ?? record.state}
                    </TableCell>
                    <TableCell>{record.snapshotVersion}</TableCell>
                    <TableCell>{record.ksefReferenceNumber ?? '-'}</TableCell>
                    <TableCell>{record.ksefInvoiceNumber ?? '-'}</TableCell>
                    <TableCell>{record.ksefSessionReferenceNumber ?? '-'}</TableCell>
                    <TableCell>{record.hasUpoFile ? 'Tak' : 'Nie'}</TableCell>
                    <TableCell>{record.upoFilePath ?? '-'}</TableCell>
                    <TableCell>{record.createdAtUtc}</TableCell>
                    <TableCell>{record.submittedAtUtc ?? '-'}</TableCell>
                    <TableCell>{record.acceptedAtUtc ?? '-'}</TableCell>
                    <TableCell>{record.rejectedAtUtc ?? '-'}</TableCell>
                    <TableCell>{record.lastErrorCode ?? '-'}</TableCell>
                  </TableRow>
                ))}
                {outgoingRecords.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={14}>
                      <Typography color="text.secondary">
                        Brak rekordów w tabeli KsefOutgoingInvoices.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
          <TablePagination
            component="div"
            count={outgoingRecordsTotalCount}
            page={outgoingRecordsPage}
            rowsPerPage={outgoingRecordsPageSize}
            onPageChange={(_, newPage) => handleOutgoingRecordsPageChange(newPage)}
            onRowsPerPageChange={(event) =>
              handleOutgoingRecordsPageSizeChange(Number.parseInt(event.target.value, 10))
            }
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Wierszy na stronę:"
          />
        </Stack>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Typography variant="h6">Akcje manualne</Typography>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label="Wybrane invoiceId"
                fullWidth
                value={selectedInvoiceId}
                onChange={(event) => setSelectedInvoiceId(event.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                label="Wybrany numer faktury"
                fullWidth
                value={selectedInvoiceNumber ?? ''}
                InputProps={{ readOnly: true }}
                helperText={
                  selectedInvoiceIdValue === null
                    ? 'Wybierz kandydaturę albo wpisz invoiceId.'
                    : !selectedCandidate && (isInvoiceLookupLoading || isInvoiceLookupFetching)
                      ? 'Ładowanie danych faktury...'
                      : selectedInvoiceNumber
                        ? 'Dane uzupełniane na podstawie wybranej faktury.'
                        : 'Brak danych faktury do podglądu numeru.'
                }
              />
            </Grid>

            {manualInvoiceLookupWarning && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="warning">
                  Nie udało się pobrać danych faktury. Możesz uzupełnić pola ręcznie.
                </Alert>
              </Grid>
            )}

            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                label="Kraj nabywcy (ISO)"
                fullWidth
                value={displayedBuyerCountryIso}
                onChange={(event) =>
                  setPayloadForm((prev) => ({
                    ...prev,
                    buyerCountryIso: toOptionalPayloadText(event.target.value),
                  }))
                }
                helperText={
                  !isEligibilityOverrideEnabled
                    ? 'Wybierz nadpisanie kwalifikacji, aby wysłać własne dane.'
                    : payloadForm.buyerCountryIso === null
                    ? 'Domyślnie z faktury.'
                    : 'Nadpisanie operatora.'
                }
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                label="NIP nabywcy"
                fullWidth
                value={displayedBuyerNip}
                onChange={(event) =>
                  setPayloadForm((prev) => ({
                    ...prev,
                    buyerNip: toOptionalPayloadText(event.target.value),
                  }))
                }
                helperText={
                  !isEligibilityOverrideEnabled
                    ? 'Wybierz nadpisanie kwalifikacji, aby wysłać własne dane.'
                    : payloadForm.buyerNip === null
                      ? 'Domyślnie z faktury.'
                      : 'Nadpisanie operatora.'
                }
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                select
                label="Czy nabywca VAT UE"
                fullWidth
                value={displayedBuyerVatEu}
                onChange={(event) =>
                  setPayloadForm((prev) => ({
                    ...prev,
                    isBuyerVatEu:
                      event.target.value === ''
                        ? null
                        : (event.target.value as KsefOutgoingPayloadForm['isBuyerVatEu']),
                  }))
                }
                helperText={
                  !isEligibilityOverrideEnabled
                    ? 'Wybierz nadpisanie kwalifikacji, aby wysłać własne dane.'
                    : payloadForm.isBuyerVatEu === null
                    ? 'Domyślnie z faktury.'
                    : 'Nadpisanie operatora.'
                }
              >
                {VAT_EU_FIELD_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                select
                label="Nadpisanie kwalifikacji"
                fullWidth
                value={payloadForm.eligibilityOverride}
                onChange={(event) =>
                  setPayloadForm((prev) => ({
                    ...prev,
                    eligibilityOverride: event.target
                      .value as KsefOutgoingPayloadForm['eligibilityOverride'],
                  }))
                }
                helperText="Brak nadpisania = użyj bieżącej polityki kwalifikacji."
              >
                {ELIGIBILITY_OVERRIDE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Powód decyzji operatora"
                fullWidth
                value={payloadForm.operatorDecisionReason}
                onChange={(event) =>
                  setPayloadForm((prev) => ({
                    ...prev,
                    operatorDecisionReason: event.target.value,
                  }))
                }
              />
            </Grid>
          </Grid>

          <Stack direction="row" spacing={2}>
            <Button disabled={busy} variant="contained" onClick={handlePrepare}>
              Przygotuj
            </Button>
            <Button disabled={busy} variant="contained" color="secondary" onClick={handleSubmit}>
              Wyślij
            </Button>
            <Button disabled={busy} variant="outlined" onClick={handlePollStatus}>
              Odśwież status
            </Button>
            <Button disabled={busy} variant="outlined" onClick={handleGetUpoUrl}>
              Pobierz URL UPO
            </Button>
            <Button disabled={busy} variant="outlined" onClick={handleGetSnapshotPreview}>
              Pobierz podgląd XML
            </Button>
            <Button
              disabled={busy}
              variant="text"
              onClick={() => {
                setPayloadForm(defaultKsefOutgoingPayloadForm);
                setLastActionLabel('');
                setLastActionResult('');
                setSnapshotPreview(null);
              }}
            >
              Wyczyść payload/wynik
            </Button>
            {busy && <CircularProgress size={24} />}
          </Stack>
        </Stack>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Typography variant="h6">Diagnostyka SHADOW</Typography>
          <Typography color="text.secondary">
            Podgląd metryk reguł i ostatnich zdarzeń semantycznych bez analizy logów serwera.
          </Typography>

          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                label="Od (czas lokalny → UTC)"
                type="datetime-local"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={semanticFromUtc}
                onChange={(event) => setSemanticFromUtc(event.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                label="Do (czas lokalny → UTC)"
                type="datetime-local"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={semanticToUtc}
                onChange={(event) => setSemanticToUtc(event.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                label="Limit zdarzeń"
                type="number"
                fullWidth
                value={semanticTake}
                onChange={(event) =>
                  setSemanticTake(Math.min(500, Math.max(1, Number(event.target.value || 1))))
                }
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Stack direction="row" spacing={2}>
                <Button variant="outlined" onClick={applySemanticFilters}>
                  Odśwież diagnostykę SHADOW
                </Button>
                <Button
                  variant="text"
                  onClick={() => {
                    refetchSemanticMetrics();
                    refetchSemanticEvents();
                  }}
                >
                  Ponów ostatnie zapytanie
                </Button>
                {(isSemanticMetricsFetching || isSemanticEventsFetching) && (
                  <CircularProgress size={24} />
                )}
              </Stack>
            </Grid>
          </Grid>

          {semanticMetricsError && (
            <Alert severity="error">
              {getKsefOutgoingErrorMessage(
                semanticMetricsError,
                'Nie udało się pobrać danych diagnostycznych SHADOW.'
              )}
            </Alert>
          )}

          {semanticEventsError && (
            <Alert severity="error">
              {getKsefOutgoingErrorMessage(
                semanticEventsError,
                'Nie udało się pobrać zdarzeń diagnostycznych SHADOW.'
              )}
            </Alert>
          )}

          <Typography variant="subtitle1">Metryki reguł</Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Id reguły</TableCell>
                  <TableCell>Poziom</TableCell>
                  <TableCell>Łączna liczba trafień</TableCell>
                  <TableCell>Odrzucone przez KSeF</TableCell>
                  <TableCell>Bez klasyfikacji</TableCell>
                  <TableCell>Potwierdzone trafienie</TableCell>
                  <TableCell>Fałszywy alarm</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(semanticMetricsResult?.rows ?? []).map((row) => (
                  <TableRow key={`${row.ruleId}-${row.severity}`}>
                    <TableCell>{row.ruleId}</TableCell>
                    <TableCell>{row.severity}</TableCell>
                    <TableCell>{row.totalHits}</TableCell>
                    <TableCell>{row.ksefRejectedHits}</TableCell>
                    <TableCell>{row.unknownAdjudicationHits}</TableCell>
                    <TableCell>{row.truePositiveHits}</TableCell>
                    <TableCell>{row.falsePositiveHits}</TableCell>
                  </TableRow>
                ))}
                {(semanticMetricsResult?.rows?.length ?? 0) === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography color="text.secondary">Brak danych metryk dla wybranego zakresu.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>

          <Typography variant="subtitle1">Ostatnie zdarzenia</Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Czas obserwacji (UTC)</TableCell>
                  <TableCell>InvoiceId</TableCell>
                  <TableCell>Id reguły</TableCell>
                  <TableCell>Poziom</TableCell>
                  <TableCell>Ścieżka pola</TableCell>
                  <TableCell>Komunikat</TableCell>
                  <TableCell>Kod KSeF</TableCell>
                  <TableCell>Werdykt</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(semanticEventsResult?.events ?? []).map((event) => (
                  <TableRow
                    key={`${event.outgoingInvoiceId}-${event.ruleId}-${event.observedAtUtc}-${event.fieldPath}`}
                  >
                    <TableCell>{event.observedAtUtc}</TableCell>
                    <TableCell>{event.invoiceId}</TableCell>
                    <TableCell>{event.ruleId}</TableCell>
                    <TableCell>{event.severity}</TableCell>
                    <TableCell>{event.fieldPath}</TableCell>
                    <TableCell>{event.message}</TableCell>
                    <TableCell>{event.ksefErrorCode ?? '-'}</TableCell>
                    <TableCell>{event.adjudicationStatus}</TableCell>
                  </TableRow>
                ))}
                {(semanticEventsResult?.events?.length ?? 0) === 0 && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography color="text.secondary">Brak zdarzeń dla wybranego zakresu.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </Stack>
      </Paper>

      <KsefOutgoingDetailsPanel
        selectedInvoiceId={selectedInvoiceId}
        selectedInvoiceNumber={selectedInvoiceNumber}
        selectedCandidate={selectedCandidate}
        snapshot={detailsSnapshot}
        stateLabelResolver={labelResolvers.stateLabel}
        decisionLabelResolver={labelResolvers.decisionLabel}
      />

      <Paper sx={{ p: 3 }}>
        <Stack spacing={1}>
          <Typography variant="h6">Podgląd XML FA(3)</Typography>
          {!snapshotPreview ? (
            <Typography data-testid="ksef-snapshot-preview-empty" color="text.secondary">
              Brak podglądu XML. Użyj „Przygotuj” lub „Pobierz podgląd XML”.
            </Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary">
                SnapshotVersion: {snapshotPreview.snapshotVersion} | Hash: {snapshotPreview.xmlHash}
              </Typography>
              <Box
                component="pre"
                data-testid="ksef-snapshot-preview-xml"
                sx={{
                  m: 0,
                  p: 2,
                  borderRadius: 1,
                  backgroundColor: 'grey.100',
                  overflowX: 'auto',
                  maxHeight: 380,
                  fontSize: 12,
                }}
              >
                {snapshotPreview.fa3SnapshotXml}
              </Box>
            </>
          )}
        </Stack>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Stack spacing={1}>
          <Typography variant="h6">Wynik akcji</Typography>
          {!lastActionResult ? (
            <Typography data-testid="ksef-action-result-empty" color="text.secondary">
              Brak wyników akcji.
            </Typography>
          ) : (
            <>
              <Typography data-testid="ksef-action-label" variant="subtitle2">
                {lastActionLabel}
              </Typography>
              <Box
                component="pre"
                data-testid="ksef-action-result-json"
                sx={{
                  m: 0,
                  p: 2,
                  borderRadius: 1,
                  backgroundColor: 'grey.100',
                  overflowX: 'auto',
                  fontSize: 12,
                }}
              >
                {lastActionResult}
              </Box>
              {(() => {
                try {
                  const parsed = JSON.parse(lastActionResult) as { downloadUrl?: string };
                  if (!parsed.downloadUrl) {
                    return null;
                  }

                  return (
                    <Link
                      data-testid="ksef-open-upo-link"
                      href={parsed.downloadUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Otwórz URL UPO
                    </Link>
                  );
                } catch {
                  return null;
                }
              })()}
            </>
          )}
        </Stack>
      </Paper>

      <KsefOutgoingTestEvidencePanel
        selectedInvoiceId={selectedInvoiceId}
        lastActionLabel={lastActionLabel}
        lastActionResult={lastActionResult}
        correlationId={parsedActionResult?.correlationId}
        finalStatus={parsedActionResult?.state}
      />
    </Stack>
  );
};

export default KsefOutgoingConsolePage;
